import { useId, useState } from "react";

/**
 * A choice from a long list: type to narrow it, then pick. A plain <select>
 * of every pupil or every copy in the school is unusable on a phone.
 */
export function SearchPick<T>({ items, value, onChange, label, text, detail, placeholder, empty, required }: {
  items: T[];
  value: string;
  onChange: (value: string) => void;
  label: (item: T) => string;
  /** What the search looks through; defaults to the label. */
  text?: (item: T) => string;
  /** A quieter second part, e.g. a pupil's class. */
  detail?: (item: T) => string;
  placeholder: string;
  empty: string;
  required?: boolean;
}) {
  const [query, setQuery] = useState("");
  const listId = useId();
  const valueOf = (item: T) => String((item as { id: number }).id);
  const shown = (() => {
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    const matches = items.filter((item) => {
      const haystack = `${(text ?? label)(item)} ${detail?.(item) ?? ""}`.toLowerCase();
      return words.every((word) => haystack.includes(word));
    });
    // Keep the chosen item visible even when the search no longer matches it.
    const chosen = items.find((item) => valueOf(item) === value);
    return chosen && !matches.includes(chosen) ? [chosen, ...matches] : matches;
  })();
  return (
    <div className="mt-1 space-y-1.5">
      <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={placeholder} aria-controls={listId} className="input" />
      <select
        id={listId}
        required={required}
        size={Math.min(6, Math.max(2, shown.length))}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="input h-auto py-1"
      >
        {!shown.length && <option disabled value="">{empty}</option>}
        {shown.slice(0, 200).map((item) => (
          <option key={valueOf(item)} value={valueOf(item)}>
            {label(item)}{detail?.(item) ? ` · ${detail(item)}` : ""}
          </option>
        ))}
      </select>
    </div>
  );
}
